import React from 'react';
import { website, websiteUrl } from '../config/config';
import { Link } from 'react-router-dom';
import SEO from '../config/SEO';

const PrivacyPolicy = () => {
     return (
          <>
               <SEO
                    title="Privacy Policy"
                    description="Read HeyPrompt privacy policy and data usage terms."
               />


               <div className="privacy-page">
                    <div className="__container">
                         <h1>Privacy Policy</h1>
                         <p className="intro">
                              At thedc tech, protecting your privacy and personal data is our highest priority. This Privacy Policy provides an in-depth explanation of the types of information we collect, how we use it, and how you can manage or request your data. By using our website, applications, or services, you consent to the collection and use of information in accordance with this policy.
                         </p>

                         <section>
                              <h2>1. Information We Collect</h2>
                              <p>
                                   We collect information to provide you with a seamless, personalized, and secure experience. Types of information include:
                              </p>
                              <ul>
                                   <li><strong>Personal Information:</strong> Your name, email, and account details submitted during registration or updates.</li>
                                   <li><strong>Usage Data:</strong> Pages visited, time spent on features, interactions, and other analytics.</li>
                                   <li><strong>Technical Information:</strong> IP addresses, device type, browser version, operating system, and cookies.</li>
                                   <li><strong>Content Submitted:</strong> Prompts, images, artworks, and text you upload or create within the platform.</li>
                                   <li><strong>Feedback and Support Queries:</strong> Emails, messages, or forms you submit to support or feedback channels.</li>
                                   <li><strong>Location Data:</strong> Optional location info you may provide for content personalization.</li>
                                   <li><strong>Third-Party Integrations:</strong> Any data shared with or received from services like Google or social logins.</li>
                                   <li><strong>Behavioral Data:</strong> Tracking your engagement patterns to enhance user experience and security.</li>
                              </ul>
                              <p>
                                   Collecting this information allows us to understand user behavior, maintain platform integrity, and provide tailored services.
                              </p>
                              <p>
                                   Some data is collected automatically, while other information comes directly from you. We never request sensitive data such as passwords or financial information outside secure authentication flows.
                              </p>
                              <p>
                                   The information helps improve our algorithms, content suggestions, and user interface for better usability and enjoyment.
                              </p>
                         </section>

                         <section>
                              <h2>2. How We Use Your Information</h2>
                              <p>
                                   Your data is used responsibly to enhance your experience and keep the platform safe and efficient. Specifically:
                              </p>
                              <ul>
                                   <li>To operate and maintain the platform and provide services you request.</li>
                                   <li>To personalize the content and suggestions you see, improving usability.</li>
                                   <li>To notify you about updates, promotions, events, or important changes.</li>
                                   <li>To analyze patterns, track engagement, and improve the platform’s features.</li>
                                   <li>To ensure account security, prevent fraud, and investigate suspicious activity.</li>
                                   <li>To enforce platform rules, terms of service, and content guidelines.</li>
                                   <li>To respond to inquiries, support requests, or legal matters efficiently.</li>
                                   <li>To maintain backup and data recovery solutions for reliability.</li>
                              </ul>
                              <p>
                                   All usage is guided by strict privacy principles to ensure your information is only used for legitimate and transparent purposes.
                              </p>
                              <p>
                                   We do not use your personal information for unrelated commercial purposes or share it with advertisers without explicit consent.
                              </p>
                         </section>

                         <section>
                              <h2>3. Sharing Your Information</h2>
                              <p>
                                   We respect your privacy and do not sell personal information. However, limited sharing is necessary for platform functionality:
                              </p>
                              <ul>
                                   <li>With trusted service providers for hosting, analytics, and platform operation.</li>
                                   <li>For legal obligations, court orders, or regulatory requirements.</li>
                                   <li>During investigations of fraud, abuse, or security violations.</li>
                                   <li>With collaborators or contractors helping maintain content moderation.</li>
                                   <li>During mergers, acquisitions, or business transfers with proper legal safeguards.</li>
                                   <li>To communicate aggregated, anonymized statistics for research or platform improvement.</li>
                                   <li>When you explicitly consent to share data for promotional or collaborative purposes.</li>
                                   <li>To protect the safety of users and the platform community against potential harm.</li>
                              </ul>
                              <p>
                                   We ensure that any third-party access is controlled and bound by contractual agreements to maintain confidentiality and security.
                              </p>
                              <p>
                                   Your personal information will never be sold to advertisers or unrelated organizations without consent.
                              </p>
                         </section>

                         <section>
                              <h2>4. Cookies and Tracking</h2>
                              <p>
                                   We use cookies, local storage, and similar technologies to improve user experience:
                              </p>
                              <ul>
                                   <li>To remember login credentials and user preferences.</li>
                                   <li>To monitor activity and performance for troubleshooting.</li>
                                   <li>To personalize suggestions, content, and notifications.</li>
                                   <li>To track feature usage and engagement trends for analysis.</li>
                                   <li>To detect suspicious or automated activity and maintain platform security.</li>
                                   <li>To integrate with analytics tools for product improvement.</li>
                                   <li>To optimize marketing communications for users who opted in.</li>
                                   <li>To provide relevant third-party integrations securely.</li>
                              </ul>
                              <p>
                                   You may disable cookies through your browser, but some features may not function fully without them.
                              </p>
                         </section>

                         <section>
                              <h2>5. Data Security</h2>
                              <p>
                                   We implement advanced security practices to safeguard your information:
                              </p>
                              <ul>
                                   <li>Encrypted storage and secure transmission protocols.</li>
                                   <li>Two-factor authentication and account verification where applicable.</li>
                                   <li>Regular audits and monitoring of our servers and databases.</li>
                                   <li>Strict access controls to limit data access to authorized personnel.</li>
                                   <li>Secure backup and recovery procedures to prevent data loss.</li>
                                   <li>Automatic detection and alerting for suspicious activities.</li>
                                   <li>Compliance with best practices for industry standards and legal requirements.</li>
                                   <li>Employee training and awareness programs on data privacy and security.</li>
                              </ul>
                              <p>
                                   While we take every precaution, no system can guarantee 100% security. We encourage users to adopt strong passwords and account protection practices.
                              </p>
                         </section>

                         <section>
                              <h2>6. Your Rights</h2>
                              <p>
                                   You have full control over your personal data and can exercise your rights at any time:
                              </p>
                              <ul>
                                   <li>Access your account data and review stored information.</li>
                                   <li>Request updates or corrections to inaccurate information.</li>
                                   <li>Request deletion of personal data subject to legal obligations.</li>
                                   <li>Withdraw consent for certain data processing operations.</li>
                                   <li>Opt out of marketing and promotional emails or messages.</li>
                                   <li>Request anonymized copies of your data for review or portability.</li>
                                   <li>Submit complaints regarding misuse or unauthorized access.</li>
                                   <li>Engage with us for transparency about third-party data sharing.</li>
                              </ul>
                         </section>

                         <section>
                              <h2>7. Third-Party Links</h2>
                              <p>
                                   Our platform may include links to external websites for reference, resources, or collaboration:
                              </p>
                              <ul>
                                   <li>We are not responsible for privacy practices of external websites.</li>
                                   <li>We encourage users to read external site policies before submitting data.</li>
                                   <li>Linked content is for convenience, research, or community engagement purposes.</li>
                                   <li>We monitor links periodically for safety and relevance.</li>
                                   <li>Third-party apps integrated with the platform follow their own privacy rules.</li>
                                   <li>External links may be included for learning or inspiration content.</li>
                                   <li>We do not control content or advertisements on third-party platforms.</li>
                                   <li>Using external links is at your own discretion and risk.</li>
                              </ul>
                         </section>

                         <section>
                              <h2>8. Children's Privacy</h2>
                              <p>
                                   Protecting minors’ privacy is critical. Our platform is intended for users aged 13 and above:
                              </p>
                              <ul>
                                   <li>We do not knowingly collect data from children under 13.</li>
                                   <li>Parents or guardians may contact us to request deletion of minors’ data.</li>
                                   <li>We implement content filters to avoid inappropriate material for minors.</li>
                                   <li>Any accidental collection of data is promptly removed.</li>
                                   <li>Children should not register without parental consent.</li>
                                   <li>Educational content is clearly marked and age-appropriate.</li>
                                   <li>We comply with COPPA and relevant laws for child protection.</li>
                                   <li>We maintain monitoring and review to ensure safe engagement.</li>
                              </ul>
                         </section>

                         <section>
                              <h2>9. Updates to This Policy</h2>
                              <p>
                                   This Privacy Policy may be updated periodically to reflect changes in services, legal requirements, or internal practices:
                              </p>
                              <ul>
                                   <li>We notify users of material updates through platform messages or emails.</li>
                                   <li>Users are encouraged to review this policy regularly.</li>
                                   <li>Minor edits may not result in individual notifications.</li>
                                   <li>Revisions are posted with a “Last Updated” date at the bottom.</li>
                                   <li>Continued use of services after updates indicates acceptance.</li>
                                   <li>Major changes affecting data collection or usage are highlighted separately.</li>
                                   <li>We maintain archival versions for transparency upon request.</li>
                                   <li>All updates adhere to applicable legal and privacy standards.</li>
                              </ul>
                         </section>

                         <section>
                              <h2>10. Contact Us</h2>
                              <p>
                                   For questions, concerns, or requests regarding this Privacy Policy, contact us:
                              </p>
                              <ul>
                                   <li>url: <Link target='_blank' to={websiteUrl}>{website}</Link></li>
                                   <li>Email: <Link target='_blank' to="mailto:thedc.here@gmail.com">thedc.here@gmail.com</Link></li>
                                   <li>Provide as much detail as possible to expedite response.</li>
                                   <li>Include account information if related to specific queries.</li>
                                   <li>Allow up to 48 hours for response depending on inquiry type.</li>
                                   <li>Feedback and complaints are reviewed promptly and respectfully.</li>
                                   <li>We value transparency and aim to resolve all privacy-related concerns efficiently.</li>
                                   <li>Our support team ensures compliance with privacy laws and internal policies.</li>
                                   <li>You may request formal records of data processing and sharing through this contact.</li>
                              </ul>
                         </section>

                         <p className="footer-note">
                              Last updated: October 7, 2025
                         </p>
                    </div>
               </div>

          </>

     );
};

export default PrivacyPolicy;
